/**
 * BhiOnboardingWizard.jsx — iter667
 *
 * 5-минутен интерактивен tour за нови потребители на BHI. Показва се САМО
 * когато `shared.sources_used = []` (т.е. пациентът няма качени данни) и
 * `bhi_onboarding_done` localStorage flag-а не е сетнат.
 *
 * 6 стъпки → след всяка ясно действие "Готово" или "Пропусни". В края
 * стартира первия upload.
 *
 * Strict Bulgarian copy. Educational only.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Sparkles, ChevronRight, ChevronLeft, X, Upload,
  Eye, Brain, Leaf, HeartHandshake, ShieldCheck, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const STEPS = [
  {
    id: "welcome",
    emoji: "👋",
    icon: Sparkles,
    color: "from-rose-500 via-pink-500 to-purple-500",
    title: "Добре дошли в Метаболитния профил на гръдната тъкан",
    body: (
      <>
        <p className="text-sm leading-relaxed mb-3">
          Тук ще създадем заедно Вашия личен <strong>огледален образ</strong> на гръдното здраве — не диагноза, а 
          {" "}<strong>биологичен пейзаж</strong>, който можете да повлияете.
        </p>
        <p className="text-sm leading-relaxed">
          В следващите 5 стъпки ще Ви покажа какво да очаквате и как BHI работи за Вас.
          <span className="opacity-70"> (Образователен помощник — не заменя медицинска консултация.)</span>
        </p>
      </>
    ),
  },
  {
    id: "assess",
    emoji: "🪞",
    icon: Sparkles,
    color: "from-rose-500 to-pink-500",
    title: "Опознаване — кой съм аз",
    body: (
      <ul className="space-y-2 text-sm">
        <li>📋 Попълвате базов профил (възраст, тегло, цикъл, фамилна история)</li>
        <li>🧬 Преминавате през DKG наследствения въпросник</li>
        <li>🎯 Получавате композиран BHI скор + образователна оценка на наследствените сигнали</li>
      </ul>
    ),
  },
  {
    id: "observe",
    emoji: "🔬",
    icon: Eye,
    color: "from-pink-500 to-purple-500",
    title: "Наблюдение — какво е сега",
    body: (
      <>
        <p className="text-sm leading-relaxed mb-2">Тук качвате реалните си данни — колкото повече, толкова по-точна е персонализацията:</p>
        <ul className="space-y-1.5 text-sm">
          <li>📷 Мамограма за гръдна плътност (BI-RADS A–D)</li>
          <li>🩸 Лабораторни изследвания и серумни хормони (TSH, естрадиол, прогестерон, SHBG, FSH, LH, пролактин, кортизол)</li>
          <li>🧪 DUTCH тест (метаболизъм на естрогена) — по избор</li>
          <li>🧬 ДНК панел (MTHFR, COMT, GST, BRCA) — по избор</li>
        </ul>
      </>
    ),
  },
  {
    id: "understand",
    emoji: "🧠",
    icon: Brain,
    color: "from-purple-500 to-violet-500",
    title: "Разбиране — какво се случва биологично",
    body: (
      <ul className="space-y-2 text-sm">
        <li>📊 Карта на 5 sub-environment-а (инсулин, IGF-1, възпаление, естроген, мастност)</li>
        <li>📖 „Разкажи ми моята детокс история“ — 2-минутно AI обяснение само за Вас</li>
        <li>🔍 Прозрачна <strong>Проверка на достоверността (Fusion Audit)</strong> — кой DUTCH/ДНК маркер коригира кой скор и защо</li>
      </ul>
    ),
  },
  {
    id: "plan",
    emoji: "🌿",
    icon: Leaf,
    color: "from-emerald-500 to-teal-500",
    title: "План — какво да правя",
    body: (
      <ul className="space-y-2 text-sm">
        <li>🌱 7-стъпков <strong>План за възстановяване на тъканта (BTRP)</strong></li>
        <li>📅 12-месечен персонализиран превантивен план (BPP)</li>
        <li>🎯 Три действия, насочени именно към Вашите показатели (не общи)</li>
      </ul>
    ),
  },
  {
    id: "act_sustain",
    emoji: "🤝",
    icon: HeartHandshake,
    color: "from-amber-500 to-orange-500",
    title: "Действие и поддържане — устойчиво",
    body: (
      <>
        <ul className="space-y-2 text-sm mb-3">
          <li>📈 Записване на седмични действия в Breast Health Coach AI</li>
          <li>📬 Месечни coach доклади + email reminder-и в правилния момент</li>
          <li>🩺 Сравнение на скоровете през времето → виждате реален прогрес</li>
        </ul>
        <p className="text-sm leading-relaxed">
          Готово! Сега ще започнете с първото качване. Препоръчвам да започнете с {" "}
          <strong>лабораторно изследване</strong> — то отнема най-малко време и веднага показва резултати.
        </p>
      </>
    ),
    finalCta: true,
  },
];

const STORAGE_KEY = "bhi_onboarding_done";

export default function BhiOnboardingWizard({ onStartUpload }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [sourcesUsed, setSourcesUsed] = useState(null);
  const [doneFlag, setDoneFlag] = useState(true);  // start true to avoid flash; flip after read

  useEffect(() => {
    try { setDoneFlag(localStorage.getItem(STORAGE_KEY) === "1"); } catch { setDoneFlag(false); }
  }, []);

  useEffect(() => {
    if (doneFlag) return;  // already completed — don't fetch
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bhi/shared-context`);
        if (active) setSourcesUsed(data?.sources_used || []);
      } catch {
        if (active) setSourcesUsed([]);
      }
    })();
    return () => { active = false; };
  }, [API, doneFlag]);

  const shouldShow = useMemo(() => {
    if (doneFlag) return false;
    if (!Array.isArray(sourcesUsed)) return false;
    return sourcesUsed.length === 0;
  }, [doneFlag, sourcesUsed]);

  useEffect(() => {
    if (shouldShow) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [shouldShow]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setDoneFlag(true);
    setOpen(false);
  }, []);

  // iter1061 — обиколката се затваря и с клавиша Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  const startUpload = useCallback(() => {
    dismiss();
    onStartUpload?.();
  }, [dismiss, onStartUpload]);

  if (!open || !shouldShow) return null;

  const step = STEPS[stepIdx];
  const Icon = step.icon;
  const isLast = stepIdx === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-testid="bhi-onboarding-wizard"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        className={`relative w-full max-w-lg rounded-3xl border shadow-2xl overflow-hidden ${
          isDark ? "bg-[hsl(222,47%,11%)] border-white/10" : "bg-white border-gray-200"
        }`}
      >
        {/* Top accent strip */}
        <div className={`h-1 bg-gradient-to-r ${step.color}`} />

        {/* Header */}
        <div className="p-5 pb-3 flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shrink-0`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-2xl">{step.emoji}</div>
            </div>
          </div>
          <button
            onClick={dismiss}
            className={`p-1.5 rounded-lg ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
            data-testid="onboarding-dismiss"
            aria-label="Затвори"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step indicator dots */}
        <div className="px-5 mb-3 flex items-center gap-1.5" data-testid="onboarding-dots">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === stepIdx ? "bg-gradient-to-r from-pink-500 to-purple-500 w-8"
                : i < stepIdx ? (isDark ? "bg-white/40 w-4" : "bg-gray-400 w-4")
                              : (isDark ? "bg-white/10 w-4" : "bg-gray-200 w-4")
              }`}
            />
          ))}
          <div className="ml-auto text-[10px] opacity-50">{stepIdx + 1} / {STEPS.length}</div>
        </div>

        {/* Body */}
        <div className="px-5 pb-4">
          <h2 className="text-xl font-extrabold mb-3 leading-tight" data-testid={`onboarding-step-${step.id}`}>
            {step.title}
          </h2>
          {step.body}
        </div>

        {/* Footer actions */}
        <div className={`px-5 py-4 flex items-center justify-between gap-2 border-t ${
          isDark ? "border-white/5 bg-white/[0.02]" : "border-gray-100 bg-gray-50/60"
        }`}>
          <button
            onClick={dismiss}
            aria-label="Затвори"
            className={`text-xs ${isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-800"}`}
            data-testid="onboarding-skip"
          >
            Пропусни обиколката
          </button>
          <div className="flex items-center gap-2">
            {stepIdx > 0 && (
              <button
                onClick={() => setStepIdx((i) => i - 1)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                  isDark ? "bg-white/5 hover:bg-white/10" : "bg-gray-100 hover:bg-gray-200"
                }`}
                data-testid="onboarding-prev"
              >
                <ChevronLeft className="w-3 h-3" /> Назад
              </button>
            )}
            {!isLast ? (
              <button
                onClick={() => setStepIdx((i) => i + 1)}
                className={`inline-flex items-center gap-1 px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r ${step.color}`}
                data-testid="onboarding-next"
              >
                Напред <ChevronRight className="w-3 h-3" />
              </button>
            ) : (
              <button
                onClick={startUpload}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90"
                data-testid="onboarding-start-upload"
              >
                <Upload className="w-3.5 h-3.5" /> Започни първото качване
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
